'use strict';

import React, { useMemo, useRef } from 'react';
import StarIcon from '@mui/icons-material/Star';
import { cn } from '@src/utils/ui';
import Divider from '@mui/material/Divider';
import { TopRatedBadge } from '@src/components/common/badges/top-rated-badge/top-rated-badge';
import { NewServiceBadge } from '@src/components/common/badges/new-service-badge/new-service-badge';
import { PromotedBadge } from '@src/components/common/badges/promoted-badge/promoted-badge';
import { Button } from '@src/components/ui/button/button';
import { useExpandableTruncation } from '@src/components/hooks/useExpandableTruncation';
import PropTypes from 'prop-types';
import { KeyboardArrowDown, CheckCircle, Home, LocationOn, Cancel } from '@mui/icons-material';
import moment from 'moment';
import { Calendar } from '@src/components/common/calendar/calendar';
import { getBlockedDayEvents } from '@src/utils/date/getBlockedDayEvents';
import { getAvailableDayEvents } from '@src/utils/date/getAvailableDayEvents';
import { createRoot } from 'react-dom/client';
import { FavoriteToggle } from './favorite-toggle';
import { LoggedInUserPropType } from '@src/components/prop-types/user';
import DOMPurify from 'isomorphic-dompurify';
import { ShareButton } from '@src/components/common/share-button/share-button';

export const InfoSection = ({ serviceMainInfo, ...props }) => {
    const formattedPrice = '$' + Number(serviceMainInfo.price).toFixed(2);
    const showBadges = Object.values(serviceMainInfo.badges).some(Boolean);
    const descRef = useRef(null);
    const { isTruncated, isExpanded, toggleExpand } = useExpandableTruncation(descRef);

    return (
        <div className='tw-space-y-[24px]'>
            <div className='ds-main_info'>
                <div className='tw-flex md:tw-gap-[16px] md:tw-items-start tw-flex-col md:tw-flex-row'>
                    <h1 className='md:tw-self-center ds-name tw-font-[700] tw-text-[24px] tw-leading-[133%] tw-tracking-[0] tw-break-all pt-[16px] tw-text-[#0050C4]'>
                        {serviceMainInfo.name}
                    </h1>
                    <div className='tw-flex tw-justify-between tw-grow tw-min-w-[250px]'>
                        <div className='tw-flex tw-gap-[8px] tw-items-center'>
                            {!props.user.Guest ? <FavoriteToggle {...props} /> : null}
                            <ShareButton />
                        </div>
                        <div className='tw-flex tw-gap-[4px] tw-grow tw-justify-end tw-items-center'>
                            <span className='tw-font-[700] tw-text-[20px] tw-leading-[160%] tw-text-[#333] tw-tracking-[0.15px]'>
                                {formattedPrice}
                            </span>
                            <span className='tw-font-[400] tw-text-[14px] tw-leading-[143%] tw-tracking=[0.17px] tw-lowercase'>
                                per {serviceMainInfo.priceUnit}
                            </span>
                        </div>
                    </div>
                </div>
                <Divider className='tw-my-[8px] lg:tw-d-none' />
                <div className='ds-icons tw-space-y-[8px]'>
                    <div className='ds-price_ratings tw-flex tw-justify-between pt-[8px] pb-[8px]'>
                        <div className={cn('tw-flex tw-justify-center tw-items-center tw-gap-[4px]')}>
                            <StarIcon size={16} className='tw-text-bcaa-yellow-900' />
                            <span className='tw-leading-[22px] tw-align-middle tw-text-[14px]'>
                                {serviceMainInfo.ratingsCount < 1 ? <i>(0) No Reviews yet</i> : serviceMainInfo.ratings}{' '}
                                ({serviceMainInfo.ratingsCount})
                            </span>
                        </div>
                    </div>
                    {serviceMainInfo.location ? (
                        <div className='ds-location tw-flex tw-items-center tw-gap-[4px]'>
                            <LocationOn weight='fill' size={16} className='tw-text-[#5A5A5A]' />
                            <span className='tw-font-[400] tw-text-[14px]  tw-break-all'>
                                {serviceMainInfo.location}
                            </span>
                        </div>
                    ) : null}
                    {serviceMainInfo.businessType ? (
                        <div className='ds-business_type tw-flex tw-items-center tw-gap-[4px]'>
                            <Home weight='fill' size={16} className='tw-text-[#5A5A5A]' />
                            <span className='tw-font-[400] tw-text-[14px] tw-break-all'>
                                {serviceMainInfo.businessType}
                            </span>
                        </div>
                    ) : null}
                </div>
                {showBadges && (
                    <div className='ds-badges tw-flex tw-items-start tw-content-start tw-gap-[8px] tw-py-[8px] tw-self-stretch tw-flex-wrap'>
                        {serviceMainInfo.badges.isTopRated && <TopRatedBadge />}
                        {serviceMainInfo.badges.isNewService && <NewServiceBadge />}
                        {serviceMainInfo.badges.isPromoted && <PromotedBadge />}
                    </div>
                )}
                <Divider className='tw-my-[8px] lg:tw-d-none' />
            </div>

            <div className='ds-description tw-space-y-[8px]'>
                <h6 className='tw-text-[#0050C4] tw-gap-[12px] tw-font-[700] tw-text-[20px] tw-leading-[160%] tw-tracking-[0.15px]'>
                    Description
                </h6>
                <div
                    ref={descRef}
                    className={cn(
                        'ck-content tw-text-[16px] tw-leading-[24px] tw-tracking-[0] tw-text-[#5A5A5A] tw-transition-all tw-break-words',
                        !isExpanded && 'tw-line-clamp-4'
                    )}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(serviceMainInfo.description) }}
                />

                {isTruncated && (
                    <Button
                        onClick={toggleExpand}
                        className={cn(
                            'tw-flex tw-justify-center tw-items-center tw-text-primary-500 tw-gap-[8px] tw-w-fit',
                            'tw-px-0 tw-py-[8px]'
                        )}
                    >
                        <KeyboardArrowDown
                            size={24}
                            className='tw-transition-transform'
                            style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }}
                        />
                        <span className='tw-font-[500] tw-text-[16px] tw-leading-[26px] tw-tracking-[0.46px] tw-uppercase'>
                            {isExpanded ? 'Show Less' : 'Show More'}
                        </span>
                    </Button>
                )}
            </div>

            <div className='ds-description tw-space-y-[8px]'>
                <h6 className='tw-gap-[12px] tw-text-[#0050C4] tw-font-[700] tw-text-[20px] tw-leading-[160%] tw-tracking-[0.15px]'>
                    Operating hours
                </h6>
                {serviceMainInfo.allDay ? (
                    <p className='tw-text-[16px]'>This tasker operates 24/7.</p>
                ) : (
                    <ul className='tw-list-disc tw-list-inside tw-mx-[8px] tw-text-[16px]'>
                        {serviceMainInfo.openingHours.map(item => (
                            <li key={item.Day}>
                                <span className='tw-inline-block tw-w-[100px]'>
                                    {moment()
                                        .day((item.Day - 1) % 7)
                                        .format('dddd')}
                                </span>
                                <span>
                                    {moment(item.StartTime, 'HH:mm:ss', true).format('h:mm A')}
                                    {' - '}
                                    {moment(item.EndTime, 'HH:mm:ss', true).format('h:mm A')}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className='ds-description tw-space-y-[8px]'>
                <h6 className='tw-gap-[12px] tw-text-[#0050C4] tw-font-[700] tw-text-[20px] tw-leading-[160%] tw-tracking-[0.15px]'>
                    Availability
                </h6>
                <CalendarView {...serviceMainInfo} />
            </div>
        </div>
    );
};

const CalendarView = ({ allDay, openingHours, blockedDates }) => {
    const events = useMemo(() => {
        const availables = allDay ? [] : openingHours;

        const unavailables = blockedDates?.map(block => ({
            ...block,
            StartDateTime: moment.unix(block.StartDateTime),
            EndDateTime: moment.unix(block.EndDateTime)
        }));

        const newEvents = {
            recurringEvents: getAvailableDayEvents(availables),
            events: getBlockedDayEvents(unavailables)
        };

        return newEvents;
    }, [allDay, openingHours, blockedDates]);

    return (
        <Calendar
            {...events}
            initialView='timeGridWeek'
            renderEventContent={eventInfo => {
                const container = document.createElement('div');
                container.className = 'tw-flex tw-items-center tw-justify-center';

                let component = null;
                if (eventInfo.event._def.extendedProps.type === 'available') {
                    component = <CheckCircle size={24} weight='fill' className='tw-text-[#057738]' />;
                } else if (['blocked', 'rest'].includes(eventInfo.event._def.extendedProps.type)) {
                    component = <Cancel size={24} weight='fill' className='tw-text-[#B0BEC5]' />;
                }

                createRoot(container).render(component);
                return { domNodes: [container] };
            }}
            renderLegend={() => (
                <>
                    <span className='tw-flex tw-items-center tw-gap-[6px]'>
                        <CheckCircle size={24} weight='fill' className='tw-text-[#057738]' />
                        Available
                    </span>
                    <span className='tw-flex tw-items-center tw-gap-[6px]'>
                        <Cancel size={24} weight='fill' className='tw-text-[#B0BEC5]' />
                        Unavailable
                    </span>
                </>
            )}
        />
    );
};

CalendarView.propTypes = {
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    priceUnit: PropTypes.string.isRequired,
    location: PropTypes.string,
    businessType: PropTypes.string,
    badges: PropTypes.shape({
        isNewService: PropTypes.bool,
        isTopRated: PropTypes.bool,
        isPromoted: PropTypes.bool
    }),
    ratings: PropTypes.number,
    ratingsCount: PropTypes.number,
    allDay: PropTypes.bool,
    openingHours: PropTypes.array,
    blockedDates: PropTypes.array
};

InfoSection.propTypes = {
    serviceMainInfo: PropTypes.shape(CalendarView.propTypes),
    user: LoggedInUserPropType
};
